import BootstrapTable from 'react-bootstrap-table-next';
import {
  managerUserCheckboxFormatter,
  managerUserDeleteFormatter,
  managerTaskTeamHeaderFormatter,
  calculateMiddleIndex,
} from '../helper';
import './manage-task.css';
import '../user-style.css';
import { PaginationFooter } from '../../Table/PaginationFooter';
import { useEffect, useRef, useState } from 'react';
import { useTeamTaskTemplate } from './useTeamTaskTemplate';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useUpdateTeamTasks } from './hooks/useUpdateTeamTasks';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { Toast } from 'react-bootstrap';
import { usePagination } from '../../Table/PaginationFooter/hooks/usePagination';

// example tasks from clinics.features
// const mockTasks = [
//   {
//     id: 1,
//     task: 'Blood pressure check',
//     teams: {
//       mental_health: false,
//       rap: false,
//       gen_med: false,
//       surgical: true,
//       med_specialist: false,
//     },
//   },
// ];

export const ManageTask = () => {
  const sort = { dataField: 'id', order: 'asc' };
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const activeId = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const { isLoading, postUpdateTeamTasks } = useUpdateTeamTasks();
  const [isDirty, setIsDirty] = useState(false);
  const { closeToast, showToast, setToast, toastMessage } = useToastMessage();
  const { selectedTenancy } = useSelector((state) => state.clinicianProfile);
  const clinics = useSelector((state) => state.clinicianProfile.clinics);

  const { columnData, teamsIdAndName, addNewTeam, deleteTask, teamIds } =
    useTeamTaskTemplate(clinics);
  const { register, handleSubmit, watch, setValue, getValues, reset } = useForm(
    {
      defaultValues: {
        teamTemplates: columnData,
        newTask: '',
      },
    },
  );
  const value = watch('teamTemplates');
  const taskRows = value.filter((item) => !item.isDeleted);
  const {
    currentPage,
    totalPages,
    pageSize,
    handleNextPage,
    handlePreviousPage,
    setPageSize,
    canNextPage,
    canPreviousPage,
  } = usePagination({
    totalCount: Array.isArray(taskRows) ? taskRows.length : 0,
  });
  const paginatedData = (Array.isArray(taskRows) ? taskRows : []).slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const handleSubmitTaskForm = async (values) => {
    const teamTasksValue = values['teamTemplates'];
    setToast('Updating tasks...', { autoHide: false });
    await postUpdateTeamTasks({
      selectedTenancy: selectedTenancy,
      tasks: teamTasksValue,
    });

    setIsDirty(false);
    setToast('Tasks updated!');
  };

  const resetTaskForm = async () => {
    reset({
      teamTemplates: columnData,
    });
    setIsDirty(false);
  };

  const handleCreatingTask = () => {
    const newTask = getValues('newTask');
    if (!!newTask) {
      const newFormItem = addNewTeam(newTask);
      const currentTaskFormValues = getValues('teamTemplates');

      const newId = newFormItem.id;
      const findDuplicatedId = currentTaskFormValues.findIndex(
        (item) => item.id === newId,
      );
      if (findDuplicatedId > -1) {
        setToast('New task not added. Task already exists.');
        return;
      }

      setValue('teamTemplates', [...currentTaskFormValues, newFormItem]);
      setValue('newTask', '');
      setIsDirty(true);
    }
  };

  const handleDeleteTask = (taskId) => {
    const updateValues = deleteTask(taskId, getValues('teamTemplates'));

    setValue('teamTemplates', updateValues);
    setIsDirty(true);
    setShowConfirmModal(false);
  };

  const columns = useMemo(() => {
    const customizeColumns = [
      {
        dataField: 'task',
        text: 'Task',
        classes: 'col16',
        headerClasses: 'user-normal-header',
        events: {
          onClick: (e, column, columnIndex, row, rowIndex) => {
            e.stopPropagation();
          },
        },
      },
    ];

    teamIds.forEach((teamId) => {
      customizeColumns.push({
        dataField: 'teams.' + teamId,
        text: teamsIdAndName[teamId],
        classes: 'col6',
        formatter: managerUserCheckboxFormatter,
        headerFormatter: (column, index) =>
          managerTaskTeamHeaderFormatter(
            column,
            index,
            calculateMiddleIndex(teamIds.length),
          ),
        headerClasses: 'user-role-header',
        events: {
          onClick: (e, column, columnIndex, row, rowIndex) => {
            e.stopPropagation();
            const taskId = row.id;
            const currentValue = getValues('teamTemplates');
            const indexToUpdate = currentValue.findIndex(
              (item) => item.id === taskId,
            );
            const toUpdateValue = !currentValue[indexToUpdate].teams[teamId];
            currentValue[indexToUpdate].teams[teamId] = toUpdateValue;
            setValue('teamTemplates', currentValue);
            setIsDirty(true);
          },
        },
      });
    });
    // }

    customizeColumns.push({
      dataField: 'id',
      text: '',
      classes: 'col3 text-center',
      headerClasses: 'user-normal-header',
      formatter: managerUserDeleteFormatter,
    });

    return customizeColumns;
  }, [teamIds]);

  const rowEvents = {
    onClick: (e, row, rowIndex) => {
      setActiveIndex(rowIndex);
      setShowConfirmModal(true);
      activeId.current = row.id;
    },
  };

  useEffect(() => {
    if (columnData)
      reset({
        teamTemplates: columnData,
        newTask: '',
      });
  }, [columnData]);

  return (
    <>
      <form
        onSubmit={handleSubmit(handleSubmitTaskForm)}
        className={isDirty ? 'offset-actions-bar' : ''}
      >
        <div className="relative">
          <BootstrapTable
            bootstrap4
            keyField="id"
            wrapperClasses="table-responsive"
            id="manage-task-table"
            remote={{ pagination: true, filter: false, sort: true }}
            bordered={false}
            // data={value.filter((item) => !item.isDeleted)}
            data={paginatedData}
            columns={columns}
            rowEvents={rowEvents}
            // rowClasses={rowClasses}
            sort={sort}
            // onTableChange={handleTableChange}
          />
          <PaginationFooter
            currentPage={currentPage}
            totalPage={totalPages}
            pageSize={pageSize}
            canNextPage={canNextPage}
            canPreviousPage={canPreviousPage}
            handleNextPage={handleNextPage}
            handlePreviousPage={handlePreviousPage}
            setPageSize={setPageSize}
          />
          <div className="manage-task-form-container">
            <div className="manage-task-heading">
              Create a task to add to the task dropdown
            </div>
            <div className="manage-task-input-container">
              <input
                className="manage-task-input"
                type="text"
                placeholder="Start typing..."
                aria-label="Create the task"
                {...register('newTask')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault(); // Prevent form submission on Enter
                  }
                }}
              />
              <button
                className="manage-task-button"
                aria-label="Create the task button"
                onClick={handleCreatingTask}
                type="button"
              >
                <i className="fa-solid fa-plus fa-lg" />
              </button>
            </div>
          </div>
          {showConfirmModal ? (
            <div
              className="delete-confirm-modal"
              style={{
                position: 'absolute',
                top: `${(activeIndex + 1) * 45}px`,
                right: '-90px',
              }}
            >
              Confirm delete?
              <div>
                <button
                  style={{ color: '#265374' }}
                  onClick={() => {
                    setShowConfirmModal(false);
                  }}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  style={{ color: '#265374' }}
                  onClick={() => {
                    handleDeleteTask(activeId.current);
                  }}
                  type="button"
                >
                  Confirm
                </button>
              </div>
            </div>
          ) : null}
        </div>
        {isDirty ? (
          <div className="settings-page-actions settings-page-actions-right-0">
            <button
              type="button"
              className="cancel-change"
              onClick={resetTaskForm}
              disabled={isLoading}
            >
              Cancel changes
            </button>
            <button type="submit" className="save-change" disabled={isLoading}>
              Save changes
            </button>
          </div>
        ) : null}
      </form>
      <Toast show={showToast} onClose={closeToast} className="team-toast">
        <Toast.Body>{toastMessage}</Toast.Body>
      </Toast>
    </>
  );
};
